import { FC, useState, useEffect } from 'react';
import { PassApproval } from 'models/IPassApprovals';
import { PassApprovalDetail } from 'models/IPassApprovalDetail';
import { Table, Icon, TableHeading, TableCell, IconButton, Text, Snackbar, Snack } from '@nike/eds';
import { useI18nContext } from '../../context/language/use-i18n-context';
import {
  ApprovalDetails,
  ApproveOrDeclinePass,
  GetPassApprovals,
} from '../../services/approvals-service';
import { dateFormat } from '../../utils/date-helper';
import { ApproveModal } from './approval-action/approval-action-modal';
import styles from './approvals.module.css';
import ts from './i18n';
import Banner from 'components/shared/banner/banner';
import { AppConfig } from './../../utils/constants';
import { ModalData } from './Type';
import { useLanguageContext } from 'context/language/context';
import { Languages } from 'models/translations';
import { ApprovalDetail, ApprovalDetailProps } from './approval-action/approval-detail-modal';
import dayjs from 'dayjs';
import { logPageAction } from 'newrelic';

type SortColumn = { key: string; asc: boolean };

type TsKeys = keyof typeof ts;

type snackStatus = 'info' | 'success' | 'warning' | 'error' | 'neutral';

export const Approvals: FC = () => {
  const [language] = useLanguageContext();
  const [error, setError] = useState<string>();
  const { i18nString } = useI18nContext();
  const [approvalList, setApprovalsList] = useState<PassApproval[]>();
  const [sorting, setSorting] = useState<SortColumn>({
    key: 'name',
    asc: true,
  });
  const [snackBar, setSnackBar] = useState({
    show: false,
    status: 'success',
    message: '',
  });
  const [modalData, setModalData] = useState<ModalData>({
    id: 0,
    open: false,
    modalType: 'approve',
    name: '',
    isChinaVendor: false,
  });

  const [reload, setReload] = useState<boolean>(false);

  const [approvalDetailProps, setApprovalDetailProps] = useState<ApprovalDetailProps>({
    passApproval: {} as PassApproval,
    passApprovaldetail: {} as PassApprovalDetail,
    showDetail: false,
    subHeader: '',
    funOnDismiss: () =>
      setApprovalDetailProps(prevProps => ({
        ...prevProps,
        showDetail: false,
      })),
    handleAction: () => (id: number, actionType: 'approve' | 'decline') => {
      handleModalAction(id, actionType);
      setApprovalDetailProps(prevProps => ({
        ...prevProps,
        showDetail: false,
      }));
    },
  });

  const columns = [
    {
      headerName: ts.GRID_HEADER_REQUESTING_EMPLOYEE.stringKey,
      field: 'firstName',
    },
    {
      headerName: ts.GRID_HEADER_REQUEST_TYPE.stringKey,
      field: 'approvalTypeToken',
    },
    {
      headerName: ts.GRID_HEADER_ORGANIZATION.stringKey,
      field: 'organization',
    },
    {
      headerName: ts.GRID_HEADER_DATE_SUBMITTED.stringKey,
      field: 'submitTimestamp',
    },
    {
      headerName: ts.GRID_HEADER_NUMBER_OF_VISITORS.stringKey,
      field: 'visitors',
    },
    { headerName: ts.GRID_HEADER_STATUS.stringKey, field: 'stillPending' },
  ];

  useEffect(() => {
    if (approvalList) {
      const sortedList = approvalList.sort((a, b) => {
        if (sorting.key === 'date') {
          const aDate = a[sorting.key] ? dayjs(a[sorting.key]) : null;
          const bDate = b[sorting.key] ? dayjs(b[sorting.key]) : null;

          if (!aDate && !bDate) return 0;
          if (!aDate) return sorting.asc ? 1 : -1;
          if (!bDate) return sorting.asc ? -1 : 1;

          return sorting.asc ? aDate.diff(bDate) : bDate.diff(aDate);
        }
        if (a[sorting.key] < b[sorting.key]) {
          return sorting.asc ? -1 : 1;
        }
        if (a[sorting.key] > b[sorting.key]) {
          return sorting.asc ? 1 : -1;
        }
        return 0;
      });
      setApprovalsList([...sortedList]);
    }
  }, [sorting]);

  useEffect(() => {
    GetPassApprovals()
      .then(approvalList => {
        setApprovalsList(filterPendingApprovals(approvalList.data) || []);
      })
      .catch(error => {
        setError('Error while retrieving approvals');
      });
  }, [snackBar, reload]);

  const filterPendingApprovals = (approvals: PassApproval[]) => {
    return approvals.filter(approval => approval.stillPending !== 0 && approval.approved === null);
  };

  const handleBtnClick = async (approvalRec: PassApproval, isApproval: boolean): Promise<void> => {
    try {
      const response = await ApprovalDetails(approvalRec.id); //.then((info) => {
      setModalData({
        id: approvalRec.id,
        open: true,
        modalType: isApproval ? 'approve' : 'decline',
        name: getName(approvalRec),
        isChinaVendor:
          response.data.passLocation === '156' &&
          approvalRec.approvalTypeId === AppConfig.passType.vendor,
      });
    } catch (error) {
      setSnackBar({
        show: true,
        status: 'error',
        message: `Error while retrieving approval details ${error}`,
      });
    }
  };

  const handleModalAction = async (
    id: number,
    actionType: 'cancel' | 'approve' | 'decline',
  ): Promise<void> => {
    setModalData(prev => ({ ...prev, open: false }));
    if (actionType !== 'cancel') {
      try {
        const response = await ApproveOrDeclinePass(id, actionType === 'approve');
        if (response.status === 200) {
          logPageAction('Approval Action', { approvalId: id, actionType: actionType });
          setSnackBar({
            show: true,
            status: 'success',
            message:
              actionType === 'approve'
                ? i18nString(ts.APPROVAL_REQUEST_APPROVED)
                : i18nString(ts.APPROVAL_REQUEST_DECLINED),
          });
        } else {
          setSnackBar({
            show: true,
            status: 'error',
            message:
              actionType === 'approve'
                ? i18nString(ts.APPROVAL_ERROR_APPROVING)
                : i18nString(ts.APPROVAL_ERROR_DECLINING),
          });
        }
      } catch (error) {
        setSnackBar({
          show: true,
          status: 'error',
          message:
            actionType === 'approve'
              ? i18nString(ts.APPROVAL_ERROR_APPROVING)
              : i18nString(ts.APPROVAL_ERROR_DECLINING),
        });
      }
    }
  };

  const getState = (pass: PassApproval) => {
    if (pass.approved === true) {
      return i18nString(ts.APPROVAL_STATUS_APPROVED);
    } else if (pass.approved === false) {
      return i18nString(ts.PASS_STATUS_DECLINED);
    } else {
      return i18nString(ts.PASS_STATUS_PENDING);
    }
  };

  const getName = (pass: PassApproval) => {
    if (language === Languages.JA) {
      return pass.lastName + ' ' + pass.firstName;
    }
    return pass.firstName + ' ' + pass.lastName;
  };

  function applySorting(key: string) {
    if (sorting.key === key) {
      setSorting({ ...sorting, asc: !sorting.asc });
    } else {
      setSorting({ key: key, asc: true });
    }
  }

  const showApprovalDetail = async (approval: PassApproval): Promise<void> => {
    try {
      const response = await ApprovalDetails(approval.id);
      setApprovalDetailProps({
        passApproval: approval,
        passApprovaldetail: response.data,
        showDetail: true,
        handleAction: (id: number, actionType: 'approve' | 'decline') => {
          handleModalAction(id, actionType);
          setApprovalDetailProps(prevProps => ({
            ...prevProps,
            showDetail: false,
          }));
        },
        subHeader: i18nString(ts[approval.approvalTypeToken as keyof typeof ts]) + ' Request',
        funOnDismiss: () => {
          setApprovalDetailProps(prevProps => ({
            ...prevProps,
            showDetail: false,
          }));

          setReload(prev => !prev); // Trigger re-fetch
        },
      });
    } catch (error) {
      setSnackBar({
        show: true,
        status: 'error',
        message: `Error while retrieving approval details ${error}`,
      });
    }
  };

  return (
    <>
      <div
        className={`eds-flex eds-flex--flex-direction-row  section-cont ${styles.approval_bannerSection}`}
      >
        <Banner width={'100%'} />
      </div>
      <section className="eds-spacing--px-48">
        <Text
          className={`eds-spacing--my-36 eds-text--align-center eds-type--display-5 txt-align-center`}
        >
          {i18nString(ts.HEADER_BAR_APPROVALS)}
        </Text>
        <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center">
          <Table className="eds-table" width="100%">
            <thead>
              <tr key="approvalHeader">
                {columns.map(column => {
                  return (
                    <TableHeading
                      className={styles.table_header}
                      data-testid={column.headerName}
                      key={column.headerName}
                      onClick={() => applySorting(column.field)}
                    >
                      {i18nString(ts[column.headerName as TsKeys])}
                      {sorting.key === column.field &&
                        (sorting.asc ? (
                          <Icon className="vertical-align" name="CaretUp" data-testid="CaretUp" />
                        ) : (
                          <Icon
                            className="vertical-align"
                            name="CaretDown"
                            data-testid="CaretDown"
                          />
                        ))}
                    </TableHeading>
                  );
                })}
                <TableHeading className={styles.table_header} key={'actions'}>
                  {i18nString(ts.GRID_HEADER_ACTIONS)}
                </TableHeading>
              </tr>
            </thead>
            <tbody>
              {approvalList &&
                approvalList?.map(approval => {
                  return (
                    <tr key={approval.id}>
                      <TableCell
                        className={styles.table_td}
                        onClick={() => showApprovalDetail(approval)}
                      >
                        {getName(approval).toUpperCase()}
                      </TableCell>
                      <TableCell className={styles.table_td}>
                        {i18nString(ts[approval.approvalTypeToken as keyof typeof ts])}
                      </TableCell>
                      <TableCell className={styles.table_td}>{approval.organization}</TableCell>
                      <TableCell className={styles.table_td}>
                        {dateFormat(approval.submitTimestamp)}
                      </TableCell>
                      <TableCell className={styles.table_td}>{approval.visitors}</TableCell>
                      <TableCell className={styles.table_td}>{getState(approval)}</TableCell>
                      <TableCell>
                        <IconButton
                          label="cancel pass"
                          aria-label="cancel pass"
                          size="medium"
                          icon={'Close'}
                          onClick={() => handleBtnClick(approval, false)}
                          variant="ghost"
                        ></IconButton>
                        <IconButton
                          label="approve pass"
                          aria-label="approve pass"
                          size="medium"
                          onClick={() => handleBtnClick(approval, true)}
                          icon={'Check'}
                          variant="ghost"
                        ></IconButton>
                      </TableCell>
                    </tr>
                  );
                })}
              {approvalList?.length === 0 && (
                <tr>
                  <TableCell className={styles.table_td} colSpan={7}>
                    {i18nString(ts.APPROVAL_GRID_EMPTY_TEXT)}
                  </TableCell>
                </tr>
              )}
              {(approvalList === undefined || error !== undefined) && (
                <tr>
                  <TableCell className={styles.table_td} colSpan={7}>
                    {error ? error : 'Loading Approvals...'}
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <ApproveModal modalData={modalData} handleAction={handleModalAction} />
        <Snackbar>
          {snackBar.show && (
            <Snack
              id="approveOrDeclineStatus"
              hideDismiss={true}
              autoDismissDuration={5000}
              status={snackBar.status as snackStatus}
              onDismiss={id => setSnackBar(prev => ({ ...prev, show: false }))}
            >
              {snackBar.message}
            </Snack>
          )}
        </Snackbar>
      </section>
      {approvalDetailProps.showDetail && (
        <ApprovalDetail
          passApproval={approvalDetailProps.passApproval}
          passApprovaldetail={approvalDetailProps.passApprovaldetail}
          showDetail={approvalDetailProps.showDetail}
          subHeader={approvalDetailProps.subHeader}
          funOnDismiss={approvalDetailProps.funOnDismiss}
          handleAction={approvalDetailProps.handleAction}
        ></ApprovalDetail>
      )}
    </>
  );
};
